import { Flex } from "antd"
import React from "react"

import { cn } from "../utils/cn.ts"

interface FixedContainerProps {
  /**
   * Drops the bottom reserve, so the page's last row can rest where the
   * support button covers it while slid out.
   */
  flushBottom?: boolean
  /**
   * For a page that paints its own background to the shell's edges (Home).
   * The column bleeds over `#main-content`'s padding itself and drops the
   * `p-1 -m-1` ring, so the page's own element can fill this scroll box
   * exactly. A page that cancels the shell's padding from the INSIDE instead
   * overshoots this scroller by the ring's width — 12px of real horizontal
   * scrollbar at every width, which is how this was found.
   */
  bleedInline?: boolean
}

export function FixedContainer({
  flushBottom = false,
  bleedInline = false,
  children
}: React.PropsWithChildren<FixedContainerProps>) {
  return (
    <div className={"grid size-full min-h-0"}>
      <Flex
        vertical
        gap={"small"}
        data-testid={"fixed-container"}
        // The support widget slides out over the bottom-right corner, so the
        // column reserves 96px below its last card — otherwise the bottom-most
        // row of actions cannot be scrolled out from under it.
        //
        // That reserve is padding, and a sticky child's travel stops at the
        // content edge above it, so anything sticking to the bottom halts 96px
        // short of the visible one. `--floating-action-inset` publishes the
        // reserve so such a child can subtract it back; `TableAddAction` does.
        className={cn(
          "min-h-0 max-h-[calc(100vh-88px)] overflow-auto",
          bleedInline
            ? "-mx-2 lg:-mx-4 -mt-(--main-content-pad-y)"
            : "p-1 -m-1",
          !flushBottom && "pb-24 [--floating-action-inset:6rem]"
        )}
      >
        {children}
      </Flex>
    </div>
  )
}
